import React, { useEffect, useRef } from 'react';

/*
 * Velaigal Cloudflare Turnstile widget.
 *
 * Renders nothing when VITE_TURNSTILE_SITE_KEY is unset — the server's
 * verifyTurnstile() (server/src/turnstile.ts) skips verification entirely
 * when its own secret is unset, so an absent site key on the client is a
 * fully supported, silent no-op (dev/test) rather than a broken widget.
 *
 * The Cloudflare script is loaded once per page load via a module-level
 * promise shared across every mount — remounting this component (e.g. the
 * parent forcing a fresh challenge after a captcha_failed response, via a
 * changed `key`) reuses the already-loaded script instead of injecting a
 * duplicate <script> tag. Rendering itself stays explicit: we hand
 * window.turnstile.render() a bare div rather than relying on its
 * data-sitekey auto-render scan, so we know exactly when the challenge
 * mounts and can remove() it on cleanup.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      if (window.turnstile) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load the captcha script.'));
      document.head.appendChild(script);
    }).catch((err: unknown) => {
      // Don't cache a dead promise — a failed load (flaky network, ad
      // blocker, CDN hiccup) must not permanently disable Turnstile for the
      // rest of the SPA session. Reset so the NEXT mount (e.g. the
      // captcha_failed remount in Apply.tsx) retries the script fetch.
      scriptPromise = null;
      throw err;
    });
  }
  return scriptPromise;
}

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  onError?: () => void;
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onToken, onError }) => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Ref mirrors of the latest callbacks so the mount effect below doesn't
  // need to depend on (and re-run for) every parent re-render that passes
  // new callback identities.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  if (!siteKey) return null;

  return <div ref={containerRef} />;
};

export default TurnstileWidget;
